import React, { useState, useRef, useEffect, useCallback } from 'react';
import Hls from 'hls.js';
import { X } from 'lucide-react';

// Хук для управления видеоплеером
const useVideoPlayer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [videoData, setVideoData] = useState({
    videoUrl: '',
    seriesTitle: ''
  });

  const openVideoPlayer = useCallback((videoUrl, seriesTitle = '') => {
    if (!videoUrl) {
      console.warn('Video URL is required');
      return;
    }
    setVideoData({ videoUrl, seriesTitle });
    setIsOpen(true);
  }, []);

  const closeVideoPlayer = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setVideoData({ videoUrl: '', seriesTitle: '' });
    }, 300);
  }, []);

  return {
    isOpen,
    videoData,
    openVideoPlayer,
    closeVideoPlayer
  };
};

// Простая модалка без Dialog
const VideoPlayerModal = ({ isOpen, onClose, videoUrl, seriesTitle }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  // Инициализация HLS плеера
  useEffect(() => {
    if (!isOpen || !videoUrl || !videoRef.current) return;

    const video = videoRef.current;
    
    // Очистка предыдущего HLS экземпляра
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Проверяем поддержку HLS
    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90
      });

      hlsRef.current = hls;

      // Используем прокси для обхода CORS
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4600';
      const proxyUrl = `${apiUrl}/video/proxy?url=${encodeURIComponent(videoUrl)}`;
      
      hls.loadSource(proxyUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(console.error);
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error('Fatal HLS error:', data);
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari нативная поддержка
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4600';
      const proxyUrl = `${apiUrl}/video/proxy?url=${encodeURIComponent(videoUrl)}`;
      video.src = proxyUrl;
      video.play().catch(console.error);
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [isOpen, videoUrl]);

  const handleClose = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      <div 
        className="relative bg-black rounded-lg overflow-hidden shadow-2xl"
        style={{ 
          width: 'min(90vw, 400px)',
          aspectRatio: '9/16'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Заголовок с кнопкой закрытия */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
          <h3 className="text-white text-sm font-medium truncate pr-2">
            {seriesTitle || 'Видео'}
          </h3>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            aria-label="Закрыть видеоплеер"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Видео плеер */}
        <div className="relative w-full h-full flex items-center justify-center">
          <video
            ref={videoRef}
            className="w-full h-full object-contain"
            controls
            autoPlay
            playsInline
            controlsList="nodownload"
            aria-label={`Видеоплеер для ${seriesTitle || 'видео'}`}
          />
        </div>
      </div>
    </div>
  );
};

// Глобальный модуль для управления видеоплеером
let globalVideoPlayerState = {
  isOpen: false,
  videoUrl: '',
  seriesTitle: '',
  openVideoPlayer: null,
  closeVideoPlayer: null
};

// Компонент-провайдер видеоплеера
export const VideoPlayerProvider = ({ children }) => {
  const { isOpen, videoData, openVideoPlayer, closeVideoPlayer } = useVideoPlayer();
  
  // Обновляем глобальное состояние
  React.useEffect(() => {
    globalVideoPlayerState.isOpen = isOpen;
    globalVideoPlayerState.videoUrl = videoData.videoUrl;
    globalVideoPlayerState.seriesTitle = videoData.seriesTitle;
    globalVideoPlayerState.openVideoPlayer = openVideoPlayer;
    globalVideoPlayerState.closeVideoPlayer = closeVideoPlayer;
  }, [isOpen, videoData, openVideoPlayer, closeVideoPlayer]);

  return (
    <>
      {children}
      <VideoPlayerModal
        isOpen={isOpen}
        onClose={closeVideoPlayer}
        videoUrl={videoData.videoUrl}
        seriesTitle={videoData.seriesTitle}
      />
    </>
  );
};

// Хук для использования глобального видеоплеера
export const useGlobalVideoPlayer = () => {
  return {
    openVideoPlayer: globalVideoPlayerState.openVideoPlayer,
    closeVideoPlayer: globalVideoPlayerState.closeVideoPlayer,
    isOpen: globalVideoPlayerState.isOpen
  };
};

// Компонент для клика по обложке сериала
export const ClickableSeriesCover = ({ 
  children, 
  videoUrl, 
  seriesTitle, 
  className = '',
  ...props 
}) => {
  const { openVideoPlayer } = useGlobalVideoPlayer();
  
  const handleClick = () => {
    if (videoUrl && openVideoPlayer) {
      openVideoPlayer(videoUrl, seriesTitle);
    }
  };

  return (
    <div
      className={`${className} ${videoUrl ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
      onClick={handleClick}
      title={videoUrl ? "Нажмите для просмотра первой серии" : "Видео недоступно"}
      {...props}
    >
      {children}
    </div>
  );
};

export default VideoPlayerProvider;
